// Distribution API Service
import { CartItem } from '../utils/cartHelpers';
import { createApiClient } from '../utils/axiosConfig';

// Ensure we always hit the /api namespace even if REACT_APP_API_URL is just the host
import { getApiBaseUrl } from '../utils/apiConfig';
const RAW_API_BASE = getApiBaseUrl();
const API_BASE_URL = `${RAW_API_BASE.replace(/\/$/, '')}/api`;

// Create axios instance with default config and auth error handling
const apiClient = createApiClient(API_BASE_URL);

export interface PublicationMatchRequest {
  geography: string[];
  industry: string[];
  subClusters: string[];
  tiers: string[];
  prId?: string;
}

export interface Publication {
  id: string;
  name: string;
  tier: number;
  domainAuthority: number;
  monthlyReach: number;
  engagementRate: number;
  topicRelevance: number;
  country: string;
  email: string;
  geographicRegions?: string[]; // Optional: regions this publication covers
}

export interface ChannelSummary {
  _id: string;
  name: string;
  country?: string;
  industry?: string;
  tier?: number;
  email?: string;
}

export interface PaymentIntentResponse {
  clientSecret: string;
  amount: number;
  currency: string;
}

export interface OrderConfirmation {
  orderId: string;
  status: string;
  trackingUrl?: string;
  estimatedPublishDate?: string;
}

// Distribution API Functions

/**
 * Match publications based on criteria
 */
export const matchPublications = async (
  criteria: PublicationMatchRequest
): Promise<Publication[]> => {
  try {
    console.log('🌐 Calling API: POST /distribution/match-publications', criteria);
    const response = await apiClient.post('/distribution/match-publications', criteria);
    
    // Handle different response structures
    const publications = response.data?.publications || response.data || [];
    
    if (!Array.isArray(publications)) {
      console.error('❌ API response is not an array:', publications);
      return [];
    }
    
    console.log(`✅ API response: ${publications.length} publications received`);
    return publications;
  } catch (error: any) {
    console.error('❌ API Error matching publications:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url
    });
    throw new Error(error.response?.data?.message || 'Failed to match publications');
  }
};

/**
 * Add item to cart (server-side cart management)
 */
export const addToCart = async (cartItem: CartItem): Promise<CartItem> => {
  try {
    const response = await apiClient.post('/distribution/cart/add', cartItem);
    return response.data.cartItem;
  } catch (error: any) {
    console.error('Error adding to cart:', error);
    throw new Error(error.response?.data?.message || 'Failed to add item to cart');
  }
};

/**
 * Get current cart
 */
export const getCart = async (): Promise<CartItem[]> => {
  try {
    const response = await apiClient.get('/distribution/cart');
    return response.data.cart;
  } catch (error: any) {
    console.error('Error fetching cart:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch cart');
  }
};

/**
 * Update cart item quantity
 */
export const updateCartItem = async (
  itemId: string,
  quantity: number
): Promise<CartItem> => {
  try {
    const response = await apiClient.put(`/distribution/cart/${itemId}`, { quantity });
    return response.data.cartItem;
  } catch (error: any) {
    console.error('Error updating cart item:', error);
    throw new Error(error.response?.data?.message || 'Failed to update cart item');
  }
};

/**
 * Remove item from cart
 */
export const removeFromCart = async (itemId: string): Promise<void> => {
  try {
    await apiClient.delete(`/distribution/cart/${itemId}`);
  } catch (error: any) {
    console.error('Error removing from cart:', error);
    throw new Error(error.response?.data?.message || 'Failed to remove item from cart');
  }
};

/**
 * Clear cart
 */
export const clearCart = async (): Promise<void> => {
  try {
    await apiClient.delete('/distribution/cart');
  } catch (error: any) {
    console.error('Error clearing cart:', error);
    throw new Error(error.response?.data?.message || 'Failed to clear cart');
  }
};

/**
 * Create Stripe payment intent
 */
export const createPaymentIntent = async (
  cartItems: CartItem[]
): Promise<PaymentIntentResponse> => {
  try {
    const response = await apiClient.post('/distribution/create-payment-intent', {
      cartItems
    });
    return response.data;
  } catch (error: any) {
    console.error('Error creating payment intent:', error);
    throw new Error(error.response?.data?.message || 'Failed to create payment intent');
  }
};

/**
 * Confirm order after successful payment
 */
export const confirmOrder = async (
  cartItems: CartItem[],
  paymentIntentId: string,
  billingDetails: any
): Promise<OrderConfirmation> => {
  try {
    const response = await apiClient.post('/distribution/confirm-order', {
      cartItems,
      paymentIntentId,
      billingDetails
    });
    return response.data;
  } catch (error: any) {
    console.error('Error confirming order:', error);
    throw new Error(error.response?.data?.message || 'Failed to confirm order');
  }
};

/**
 * Send PR to matched publications
 */
export const sendPRToPublications = async (
  orderId: string,
  prContent: string,
  publications: Publication[]
): Promise<{ success: boolean; sentTo: string[] }> => {
  try {
    const response = await apiClient.post('/distribution/send-pr', {
      orderId,
      prContent,
      publications
    });
    return response.data;
  } catch (error: any) {
    console.error('Error sending PR:', error);
    throw new Error(error.response?.data?.message || 'Failed to send PR to publications');
  }
};

/**
 * Get order status
 */
export const getOrderStatus = async (orderId: string): Promise<any> => {
  try {
    const response = await apiClient.get(`/distribution/orders/${orderId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching order status:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch order status');
  }
};

/**
 * Get available distribution channels (used for media CRM)
 */
export const getChannels = async (): Promise<ChannelSummary[]> => {
  try {
    const response = await apiClient.get('/distribution/channels');
    return response.data.channels || [];
  } catch (error: any) {
    console.error('Error fetching channels:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch channels');
  }
};

/**
 * Publisher submits the final published article URL for a distribution
 */
export const submitPublishedUrl = async (
  distributionId: string,
  publishedURL: string
): Promise<void> => {
  try {
    await apiClient.post('/distribution/update-url', {
      distributionId,
      publishedURL
    });
  } catch (error: any) {
    console.error('Error submitting published URL:', error);
    throw new Error(error.response?.data?.message || 'Failed to submit published URL');
  }
};

/**
 * AI-powered ranking of publications based on PR content
 */
export const aiRankPublications = async (
  publications: Publication[],
  prId?: string,
  prContent?: string
): Promise<Publication[]> => {
  try {
    console.log('🤖 Calling AI ranking API for', publications.length, 'publications');
    const response = await apiClient.post('/distribution/ai-rank-publications', {
      publications,
      prId,
      prContent
    });
    
    const rankedPublications = response.data?.publications || [];
    const aiRanked = response.data?.aiRanked || false;
    
    if (aiRanked) {
      console.log(`✅ AI ranked ${rankedPublications.length} publications`);
    } else {
      console.log('⚠️ AI ranking not available, using default order');
    }
    
    return rankedPublications;
  } catch (error: any) {
    console.error('❌ Error calling AI ranking API:', error);
    // Return publications as-is if AI ranking fails
    return publications;
  }
};

/**
 * Get user's publication wishlist
 */
export const getUserWishlist = async (): Promise<{
  wishlist: string[];
  createdAt?: string;
  hasWishlist: boolean;
}> => {
  try {
    console.log('📋 Fetching user wishlist...');
    const response = await apiClient.get('/distribution/wishlist');
    console.log('✅ Wishlist retrieved:', response.data);
    return {
      wishlist: response.data.wishlist || [],
      createdAt: response.data.createdAt,
      hasWishlist: response.data.hasWishlist || false
    };
  } catch (error: any) {
    console.error('❌ Error fetching wishlist:', error);
    return { wishlist: [], hasWishlist: false };
  }
};

/**
 * Save user's publication wishlist
 */
export const saveUserWishlist = async (publicationIds: string[]): Promise<{
  success: boolean;
  wishlist: string[];
  isFirstTime?: boolean;
}> => {
  try {
    console.log('💾 Saving wishlist:', publicationIds);
    const response = await apiClient.post('/distribution/wishlist', { publicationIds });
    console.log('✅ Wishlist saved:', response.data);
    return {
      success: response.data.success,
      wishlist: response.data.wishlist || [],
      isFirstTime: response.data.isFirstTime
    };
  } catch (error: any) {
    console.error('❌ Error saving wishlist:', error);
    throw new Error(error.response?.data?.message || 'Failed to save wishlist');
  }
};

export default {
  matchPublications,
  addToCart,
  getCart,
  updateCartItem,
  removeFromCart,
  clearCart,
  createPaymentIntent,
  confirmOrder,
  sendPRToPublications,
  getOrderStatus,
  getChannels,
  submitPublishedUrl,
  aiRankPublications,
  getUserWishlist,
  saveUserWishlist
};




